import { isMarkupSceneObject } from "@custom-types/slide-nodes-type-guards";
import { AnnotationStatus } from "@faro-lotv/ielement-types";
import { isAnnotationStatus } from "@faro-lotv/service-wires";
import { DateTimeUtils } from "@stellar/utils";
import { getMeasurements } from "@utils/vector-utils";
import { convertArea, convertDistance } from "@utils/unit-utils";

/**
 * @returns An array of Markups sorted like this:
 * - Markups with most recently modified should show first
 *
 * @param markups Array of Markup entities.
 */
export function sortMarkups(markups) {
  return markups.sort((a, b) => {
    const aModifiedAt = new Date(a.modifiedAt).getTime();
    const bModifiedAt = new Date(b.modifiedAt).getTime();
    return bModifiedAt - aModifiedAt;
  });
}
/** Finds the slideNode and scene object associated to the markup */
export function findSlideNodeAndSceneObject(slideNodes, markup, model3d) {
  for (const slideNode of slideNodes) {
    const sceneObject = findSceneObject(slideNode, markup, model3d);
    if (sceneObject) {
      return {
        slideNode,
        sceneObject
      };
    }
    const nestedSlideNodes = slideNode.slideNodes;
    if (nestedSlideNodes) {
      const result = findSlideNodeAndSceneObject(nestedSlideNodes, markup, model3d);
      if (result) {
        return result;
      }
    }
  }
}

/** Finds the scene object associated to the markup */
function findSceneObject(slideNode, markup, model3d) {
  const sceneObjects = slideNode.nodes;
  const markupSceneObjects = sceneObjects.filter(isMarkupSceneObject);

  // 1st attempt to find scene object by the md5Hash property
  let foundSceneObject = findSceneObjectByMd5Hash(markupSceneObjects, model3d);
  if (foundSceneObject) {
    return foundSceneObject;
  }

  // As last resort attempt to find scene object by name + description
  foundSceneObject = findSceneObjectByNameAndDescription(markupSceneObjects, markup);
  return foundSceneObject;
}

/** Finds an scene object by the "md5Hash" property */
function findSceneObjectByMd5Hash(markupSceneObjects, model3d) {
  // Get the "md5Hash" property associated to the markup. This property, if available, has the same value
  // as the "md5hash" property of the scene object associated to the project.
  const md5Hash = model3d?.md5Hash;

  // Early exit if "md5Hash" value is not available
  if (!md5Hash) {
    return;
  }
  return markupSceneObjects.find(sceneObject => sceneObject.md5hash === md5Hash);
}

/** Finds an scene object by the name and description properties */
function findSceneObjectByNameAndDescription(markupSceneObjects, markup) {
  const name = markup.name.trim();
  const description = (markup.descr ?? "").trim();
  const nameAndDescription = `${name}${description}`;
  return markupSceneObjects.find(sceneObject => {
    const sceneObjectName = sceneObject.displayName.trim();
    const sceneObjectDescription = (sceneObject.markupData.description ?? "").trim();
    const sceneObjectNameAndDescription = `${sceneObjectName}${sceneObjectDescription}`;
    return nameAndDescription === sceneObjectNameAndDescription;
  });
}

/** Returns true iff the markup status value is "Resolved" */
export function isMarkupResolved(markup) {
  return markup.status?.value === AnnotationStatus.Resolved;
}

/** Returns true iff the markup due date value is defined and is overdue */
export function isMarkupOverdue(markup) {
  const dueDate = markup.dueDate?.value;

  // Markups without due date or with "Resolved" status can't be overdue
  if (!dueDate || isMarkupResolved(markup)) {
    return false;
  }
  return DateTimeUtils.isDateOverdue(dueDate);
}

/** Returns true iff any of the markups has a status other than unclassified */
export function areMarkupStatusesUsed(markups) {
  return markups.some(({
    status
  }) => isAnnotationStatus(status?.value) && status.value !== AnnotationStatus.Unclassified);
}

/** Returns true iff any of the markups has a due date */
export function areMarkupDueDatesUsed(markups) {
  return markups.some(({
    dueDate
  }) => !!dueDate?.value);
}

/** Returns true iff any of the markups has an assignee */
export function areMarkupAssigneesUsed(markups) {
  return markups.some(({
    assignee
  }) => !!assignee?.values?.[0]);
}
export function getUniqueMarkupProperties({
  markups,
  uniqueIdKey
}) {
  const allProps = markups.flatMap(markup => markup[uniqueIdKey]).filter(prop => {
    return prop !== null && prop !== undefined;
  });
  const uniqueIds = [...new Set(allProps.map(prop => {
    if (prop && typeof prop === "object" && "id" in prop) {
      return prop.id;
    }
    return undefined;
  }))];

  // All the scenes that are unique
  return uniqueIds.reduce((uniqueProps, uniqueId) => {
    const foundProp = allProps.find(prop => {
      if (prop && typeof prop === "object" && "id" in prop) {
        return prop.id === uniqueId;
      }
      return false;
    });
    if (foundProp) {
      uniqueProps.push(foundProp);
    }
    return uniqueProps;
  }, []);
}

/** Props for the `getMarkupMeasurements` function */

/** Return object of the `getMarkupMeasurements` function */

/**
 * Gets the measurement values of a measure polygon in the specified distance and area units
 * @returns An object with the measurement values as readable strings
 */
export function getMarkupMeasurements({
  measurePolygon,
  worldMatrix4Tuple,
  unitMeasurement
}) {
  // Get the raw measurement values in the default units (meters for distance, square meters for area)
  const {
    length,
    area,
    horizontal,
    vertical,
    x,
    y,
    z
  } = getMeasurements({
    localPoints: measurePolygon.points,
    isClosed: measurePolygon.isClosed,
    worldMatrix4Tuple
  });

  // Convert values to the specified unit
  const convertedLength = convertDistance(length, unitMeasurement);
  const convertedArea = typeof area === "number" ? convertArea(area, unitMeasurement) : undefined;
  const convertedHorizontal = typeof horizontal === "number" ? convertDistance(horizontal, unitMeasurement) : undefined;
  const convertedVertical = typeof vertical === "number" ? convertDistance(vertical, unitMeasurement) : undefined;
  const convertedX = typeof x === "number" ? convertDistance(x, unitMeasurement) : undefined;
  const convertedY = typeof y === "number" ? convertDistance(y, unitMeasurement) : undefined;
  const convertedZ = typeof z === "number" ? convertDistance(z, unitMeasurement) : undefined;

  // If the measurement is 2D pano image (e.g. taken from a 360 image) then the measurement
  // is indicated as an estimation. For this case we append a "~" symbol before the value.
  const isEstimatedFrom2DPano = !!measurePolygon.metaDataMap?.isEstimatedFrom2DPano;
  const formattedLength = isEstimatedFrom2DPano ? `~${convertedLength}` : convertedLength;
  const formattedArea = isEstimatedFrom2DPano && typeof convertedArea === "string" ? `~${convertedArea}` : convertedArea;
  const formattedHorizontal = isEstimatedFrom2DPano && typeof convertedHorizontal === "string" ? `~${convertedHorizontal}` : convertedHorizontal;
  const formattedVertical = isEstimatedFrom2DPano && typeof convertedVertical === "string" ? `~${convertedVertical}` : convertedVertical;
  const formattedX = isEstimatedFrom2DPano && typeof convertedX === "string" ? `~${convertedX}` : convertedX;
  const formattedY = isEstimatedFrom2DPano && typeof convertedY === "string" ? `~${convertedY}` : convertedY;
  const formattedZ = isEstimatedFrom2DPano && typeof convertedZ === "string" ? `~${convertedZ}` : convertedZ;
  return {
    isClosed: measurePolygon.isClosed,
    length: formattedLength,
    area: formattedArea,
    horizontal: formattedHorizontal,
    vertical: formattedVertical,
    x: formattedX,
    y: formattedY,
    z: formattedZ
  };
}